<template>
    <div class="rmf-form">
        <p class="rmf-form__heading" v-text="$t('RATE_FLOWERS_FORM_AND_WHEN')"></p>

        <div class="input">
            <star-rating-field
                :label="$t('RATE_FLOWERS_FORM_QUALITY_LABEL')"
                :max-stars="5"
                v-model="form.quality">
            </star-rating-field>
            <p class="input__message" v-if="$v.form.quality.$error" v-text="$t('RATE_FLOWERS_FORM_PLEASE_SELECT_QUALITY')"></p>
        </div>

        <div class="input">
            <star-rating-field
                :label="$t('RATE_FLOWERS_FORM_STYLE_LABEL')"
                :max-stars="5"
                v-model="form.style">
            </star-rating-field>
            <p class="input__message" v-if="$v.form.style.$error" v-text="$t('RATE_FLOWERS_FORM_PLEASE_SELECT_STYLE')"></p>
        </div>

        <div class="input">
            <star-rating-field
                :label="$t('RATE_FLOWERS_FORM_VALUE_LABEL')"
                :max-stars="5"
                v-model="form.value">
            </star-rating-field>
            <p class="input__message" v-if="$v.form.value.$error" v-text="$t('RATE_FLOWERS_FORM_PLEASE_SELECT_VALUE')"></p>
        </div>

        <div class="input">
            <star-rating-field
                :label="rateDeliveryText"
                :max-stars="5"
                v-model="form.delivery">
            </star-rating-field>
            <p class="input__message" v-if="$v.form.delivery.$error" v-text="$t('RATE_FLOWERS_FORM_PLEASE_SELECT_DELIVERY')"></p>
        </div>

        <div class="image-uploader" :class="{'image-uploader-active' : showImageUploader}">
             <p class="header-text" v-text="$t('RATE_FLOWERS_UPLOAD_FILE')"></p>
            <upload-image @change="onFileUpload"></upload-image>
        </div>

        <div class="input">
            <label for="comments" class="input__label" v-html="additionalCommentsLabel"></label>
            <textarea
                rows="2"
                id="comments"
                v-model="form.comments"
                class="input__textarea">
            </textarea>
        </div>

        <!--Errors-->
        <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
            <ul class="error list-disc text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"></span>:
                    <span class="font-normal" v-text="errorMsg"></span>
                </li>
            </ul>
        </div>

        <!--Actions-->
        <div class="text-center">
            <button
                class="btn btn--primary rmf-form__submit-button"
                @click="validateStep"
                :disabled="isFormProcessing" v-text="$t('RATE_FLOWERS_FORM_FEEDBACK')"></button>
        </div>

        <!--Success modal-->
        <rated-arrangement-modal
            v-if="showSuccessModal"
            @closed="showSuccessModal = false"
            :total-rating="totalRating">
        </rated-arrangement-modal>
    </div>
</template>

<script>
import between from 'vuelidate/src/validators/between';
import required from 'vuelidate/src/validators/required';
import CmsService from '@/services/cms/CmsService';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import StarRatingField from '@/components/forms/fields/StarRatingField';
import RatedArrangementModal from '@/components/modals/RatedArrangementModal';
import UploadImage from '@/components/forms/fields/UploadImage';
import { couriers } from '@/content/couriers';

export default {
    name: 'RateFlowersForm',
    data() {
        return {
            form: {
                quality: null,
                style: null,
                value: null,
                delivery: null,
                comments: null,
            },
            isFormProcessing: false,
            errorHandler: new ErrorResponseHandler(),
            showSuccessModal: false,
            file: null,
        };
    },
    props: {
        orderId: {
            required: true,
            type: Number,
        },
        courierName: {
            required: true,
            type: String,
        },
    },
    validations: {
        form: {
            quality: {
                required: required,
                between: between(1, 5),
            },
            style: {
                required: required,
                between: between(1, 5),
            },
            value: {
                required: required,
                between: between(1, 5),
            },
            delivery: {
                required: required,
                between: between(1, 5),
            },
        },
    },
    computed: {
        areAllFormFieldsValid() {
            return !this.$v.$invalid;
        },
        totalRating() {
            return this.form.quality + this.form.style + this.form.value + this.form.delivery;
        },
        showImageUploader() {
            const categoriesToWatch = [this.form.quality, this.form.style, this.form.value];
            const lowRatingOnWatchedCategory = categoriesToWatch.includes(1) || categoriesToWatch.includes(2);
            return lowRatingOnWatchedCategory;
        },
        rateDeliveryText() {
            const courierDisplayName = couriers[this.courierName] && couriers[this.courierName].RmfName;
            return courierDisplayName ? this.$t('RATE_FLOWERS_FORM_DELIVERY_LABEL', { courier: courierDisplayName }) : this.$t('RATE_FLOWERS_FORM_DELIVERY_LABEL_FALLBACK');
        },
        starRatedCategories() {
            const formInputs = Object.keys(this.form);
            const ratedCategories = formInputs.filter((key) => {
                const commentField = key === 'comments';
                return !commentField;
            });
            return ratedCategories;
        },
        additionalCommentsLabel() {
            const lowRatedCategories = this.starRatedCategories.filter((key) => {
                const lowRatedCategory = this.form[key] !== null && this.form[key] <= 2;
                return lowRatedCategory;
            });
            const numberOfLowRatings = lowRatedCategories.length;
            if (numberOfLowRatings) {
                const ratingsToBeCommaSeperated = lowRatedCategories.slice(0, numberOfLowRatings - 1);
                const lowRatingsList = numberOfLowRatings > 1 ? `<strong>${ratingsToBeCommaSeperated.join('</strong>, <strong>')}</strong> & <strong>${lowRatedCategories[numberOfLowRatings - 1]}</strong>` : `<strong>${lowRatedCategories[0]}</strong>`;
                const dynamicFeedbackQuestion = this.$t('RATE_FLOWERS_FORM_LOW_RATING_COMMENTS', { lowRatedCategories: lowRatingsList });
                return dynamicFeedbackQuestion;
            }
            return this.$t('RATE_FLOWERS_FORM_COMMENTS');
        },
    },
    methods: {
        forceRequiredValidationMessagesVisibility() {
            this.form.quality = (this.form.quality !== null) ? this.form.quality : 0;
            this.form.style = (this.form.style !== null) ? this.form.style : 0;
            this.form.value = (this.form.value !== null) ? this.form.value : 0;
            this.form.delivery = (this.form.delivery !== null) ? this.form.delivery : 0;
        },

        toggleFormProcessing() {
            this.isFormProcessing = !this.isFormProcessing;
        },

        submit() {
            let cmsService = new CmsService();

            cmsService.rateArrangementForOrder(
                this.orderId, {
                    quality: this.form.quality,
                    style: this.form.style,
                    value: this.form.value,
                    delivery: this.form.delivery,
                    comments: this.form.comments,
                    feedbackImage: this.file,
                })
                .then(() => {
                    this.showSuccessModal = true;
                })
                .catch(error => this.errorHandler.setResponse(error.response.data));
        },

        validateStep() {
            this.toggleFormProcessing();
            this.$v.$touch();
            this.areAllFormFieldsValid ? this.submit() : this.forceRequiredValidationMessagesVisibility();
            this.toggleFormProcessing();
        },

        onFileUpload(file) {
            this.file = file;
        },
    },
    components: {
        StarRatingField,
        RatedArrangementModal,
        UploadImage,
    },
};
</script>

<style lang="scss" scoped>
    .rmf-form {
        &__heading {
            margin: 24px 0 12px;
            padding: 0 12px;
            font-size: 20px;
        }

        &__submit-button {
            margin-top: 8px;
        }

        .inputSwitch {
            &__slider {
                height: 40px;
                width: 95px;

                &:before {
                    height: 30px;
                    width: 30px;
                    left: 18px;
                    bottom: 3px;
                }

                &:after {
                    @apply font-light absolute;
                    top: 5px;
                }
            }

            &__slider, input:checked + .inputSwitch__slider {
                @apply bg-white border-solid border-2;

                &:before {
                    @apply bg-success;
                    transform: translateX(33px);
                }

                &:after {
                    content: "Yes";
                    left: 16px;
                }
            }

            input:not(:checked) + .inputSwitch__slider {
                &:before {
                    @apply bg-red-700;
                    left: 8px;
                    transform: translateX(0px);
                }

                &:after {
                    content: "No";
                    left: 46px;
                }
            }
        }
    }

    .image-uploader {
        max-height: 0;
        overflow-y: hidden;
        transition: max-height 1s ease-in-out;
    }

    .image-uploader-active {
        max-height: 500px;
    }

    .header-text {
        margin-top: 0.5rem;
        padding-left: 0.75rem;
    }

</style>
